
/* === ELMEDA: Load Overpass font asynchronously (non-blocking) === */
(function() {
  var link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Overpass:ital,wght@0,100..900;1,100..900&display=swap';
  link.media = 'print';
  link.onload = function() { this.media = 'all'; };
  document.head.appendChild(link);
})();

/* ELMEDA Nav: Force open details + inject clickable ELMEDA link */
(function(){
  function initNav(){
    if(window.innerWidth>=750){
      // Force details open for desktop nav
      var d=document.getElementById('Details-menu-drawer-container');
      if(d&&!d.open){d.setAttribute('open','');d.open=true;}
      
      // Inject clickable ELMEDA link (replacing CSS ::before)
      var nav=document.querySelector('.menu-drawer__navigation');
      if(nav&&!document.getElementById('elmeda-brand-link')){
        var a=document.createElement('a');
        a.id='elmeda-brand-link';
        a.href='/';
        a.textContent='E  L  M  E  D  A';
        a.style.cssText='font-family:var(--font-heading--family,Montserrat,sans-serif);font-size:1.5rem;font-weight:700;letter-spacing:0.15em;color:#1a1a1a;margin-right:2.5rem;white-space:nowrap;text-decoration:none;flex-shrink:0;';
        nav.insertBefore(a,nav.firstChild);
      }
    }
  }
  if(document.readyState==='loading'){document.addEventListener('DOMContentLoaded',initNav);}
  else{initNav();}
  window.addEventListener('resize',initNav);
})();

/**
 * Request an idle callback or fallback to setTimeout
 * @returns {function} The requestIdleCallback function
 */
export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;

/**
 * Returns a promise that resolves after yielding to the main thread.
 * @see https://web.dev/articles/optimize-long-tasks#scheduler-yield
 */
export const yieldToMainThread = () => {
  if ('yield' in scheduler) {
    // @ts-ignore - TypeScript doesn't recognize the yield method yet.
    return scheduler.yield();
  }

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      setTimeout(resolve, 0);
    });
  });
};

/**
 * Tells if we are on a low power device based on the number of CPU cores and RAM
 * @returns {boolean} True if the device is a low power device, false otherwise
 */
export function isLowPowerDevice() {
  return Number(navigator.hardwareConcurrency) <= 2 || Number(navigator.deviceMemory) <= 2;
}

/**
 * Check if the browser supports View Transitions API
 * @returns {boolean} True if the browser supports View Transitions API, false otherwise
 */
export function supportsViewTransitions() {
  return typeof document.startViewTransition === 'function';
}

/**
 * The current view transition
 * @type {{ current: Promise<void> | undefined }}
 */
export const viewTransition = {
  current: undefined,
};

/**
 * Functions to run when a view transition of a given type is started
 * @type {{ [key: string]: () => Promise<(() => void) | undefined> }}
 */
const viewTransitionTypes = {
  'product-grid': async () => {
    const grid = document.querySelector('.product-grid');
    const productCards = /** @type {HTMLElement[]} */ ([
      ...document.querySelectorAll('.product-grid .product-grid__item'),
    ]);

    if (!grid || !productCards.length) return;

    await new Promise((resolve) =>
      requestIdleCallback(() => {
        const cardsToAnimate = getCardsToAnimate(grid, productCards);

        productCards.forEach((card, index) => {
          if (index < cardsToAnimate) {
            card.style.setProperty('view-transition-name', `product-card-${card.dataset.productId}`);
          } else {
            card.style.setProperty('content-visibility', 'hidden');
          }
        });

        resolve(null);
      })
    );

    return () =>
      productCards.forEach((card) => {
        card.style.removeProperty('view-transition-name');
        card.style.removeProperty('content-visibility');
      });
  },
};

/**
 * Starts a view transition
 * @param {() => void} callback The callback to call when the view transition starts
 * @param {string[]} [types] The types of view transition to use
 * @returns {Promise<void>} A promise that resolves when the view transition finishes
 */
export function startViewTransition(callback, types) {
  // Check if the API is supported and transitions are desired
  if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
    callback();
    return Promise.resolve();
  }

  // eslint-disable-next-line no-async-promise-executor
  return new Promise(async (resolve) => {
    let cleanupFunctions = [];

    if (types) {
      for (const type of types) {
        if (viewTransitionTypes[type]) {
          const cleanupFunction = await viewTransitionTypes[type]();
          if (cleanupFunction) cleanupFunctions.push(cleanupFunction);
        }
      }
    }

    const transition = document.startViewTransition(callback);

    if (!viewTransition.current) {
      viewTransition.current = transition.finished;
    }

    if (types) types.forEach((type) => transition.types.add(type));

    transition.finished.then(() => {
      viewTransition.current = undefined;
      cleanupFunctions.forEach((cleanupFunction) => cleanupFunction());
      resolve();
    });
  });
}

/**
 * @typedef {{ [key: string]: string | undefined }} Headers
 */

/**
 * @typedef {Object} FetchConfig
 * @property {string} method
 * @property {Headers} headers
 * @property {string | FormData | undefined} [body]
 */

/**
 * Creates a fetch configuration object
 * @param {string} [type] The type of response to expect
 * @param {Object} [config] The config of the request
 * @param {FetchConfig['body']} [config.body] The body of the request
 * @param {FetchConfig['headers']} [config.headers] The headers of the request
 * @returns {RequestInit} The fetch configuration object
 */
export function fetchConfig(type = 'json', config = {}) {
  /** @type {Headers} */
  const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

  if (type === 'javascript') {
    headers['X-Requested-With'] = 'XMLHttpRequest';
    delete headers['Content-Type'];
  }

  return {
    method: 'POST',
    headers: /** @type {HeadersInit} */ (headers),
    body: config.body,
  };
}

/**
 * Creates a debounced function that delays calling the provided function (fn)
 * until after wait milliseconds have elapsed since the last time
 * the debounced function was invoked. The returned function has a .cancel()
 * method to cancel any pending calls.
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to debounce
 * @param {number} wait The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A debounced version of fn with a .cancel() method
 */
export function debounce(fn, wait) {
  /** @type {number | undefined} */
  let timeout;

  /** @param {...any} args */
  function debounced(...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn.apply(this, args), wait);
  }

  // Add the .cancel method:
  debounced.cancel = () => {
    clearTimeout(timeout);
  };

  return /** @type {T & { cancel(): void }} */ (debounced);
}

/**
 * Creates a throttled function that calls the provided function (fn) at most once per every wait milliseconds
 *
 * @template {(...args: any[]) => any} T
 * @param {T} fn The function to throttle
 * @param {number} delay The time (in milliseconds) to wait before calling fn
 * @returns {T & { cancel(): void }} A throttled version of fn with a .cancel() method
 */
export function throttle(fn, delay) {
  let lastCall = 0;

  /** @param {...any} args */
  function throttled(...args) {
    const now = performance.now();
    // If the time since the last call exceeds the delay, execute the callback
    if (now - lastCall >= delay) {
      lastCall = now;
      fn.apply(this, args);
    }
  }

  throttled.cancel = () => {
    lastCall = performance.now();
  };

  return /** @type {T & { cancel(): void }} */ (throttled);
}

/**
 * A media query for reduced motion
 * @type {MediaQueryList}
 */
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

/**
 * Check if the user prefers reduced motion
 * @returns {boolean} True if the user prefers reduced motion, false otherwise
 */
export function prefersReducedMotion() {
  return reducedMotion.matches;
}

/**
 * Normalize a string
 * @param {string} str The string to normalize
 * @returns {string} The normalized string
 */
export function normalizeString(str) {
  return str
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Format a money value
 * @param {string} value The value to format
 * @returns {string} The formatted value
 */
export function formatMoney(value) {
  let valueWithNoSpaces = value.replace(' ', '');
  if (valueWithNoSpaces.indexOf(',') === -1) return valueWithNoSpaces;
  if (valueWithNoSpaces.indexOf(',') < valueWithNoSpaces.indexOf('.')) return valueWithNoSpaces.replace(',', '');
  if (valueWithNoSpaces.indexOf('.') < valueWithNoSpaces.indexOf(','))
    return valueWithNoSpaces.replace('.', '').replace(',', '.');
  if (valueWithNoSpaces.indexOf(',') !== -1) return valueWithNoSpaces.replace(',', '.');

  return valueWithNoSpaces;
}

/**
 * Check if the document is ready/loaded and call the callback when it is.
 * @param {() => void} callback The function to call when the document is ready.
 */
export function onDocumentLoaded(callback) {
  if (document.readyState === 'complete') {
    callback();
  } else {
    window.addEventListener('load', callback);
  }
}

/**
 * Check if the DOM is ready and call the callback when it is.
 * This fires when the DOM is fully parsed but before all resources are loaded.
 * @param {() => void} callback The function to call when the DOM is ready.
 */
export function onDocumentReady(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Removes will-change from an element after an animation ends.
 * Intended to be used as an animationend event listener.
 * @param {AnimationEvent} event The animation event.
 */
export function removeWillChangeOnAnimationEnd(event) {
  const target = event.target;
  if (target && target instanceof HTMLElement) {
    target.style.setProperty('will-change', 'unset');
    target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
  }
}

/**
 * Wait for all animations to finish before calling the callback.
 * @param {Element | Element[]} elements The element(s) whose animations to wait for.
 * @param {() => void} [callback] The function to call when all animations are finished.
 * @param {Object} [options] The options to pass to `Element.getAnimations`.
 * @returns {Promise<void>} A promise that resolves when all animations are finished.
 */
export function onAnimationEnd(elements, callback, options = { subtree: true }) {
  const animations = Array.isArray(elements)
    ? elements.flatMap((element) => element.getAnimations(options))
    : elements.getAnimations(options);
  const animationPromises = animations.reduce((acc, animation) => {
    // Ignore ViewTimeline animations
    if (animation.timeline instanceof DocumentTimeline) {
      acc.push(animation.finished);
    }

    return acc;
  }, /** @type {Promise<Animation>[]} */ ([]));

  return Promise.allSettled(animationPromises).then(callback);
}

/**
 * Check if the click is outside the element.
 * @param {MouseEvent} event The mouse event.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the click is outside the element, false otherwise.
 */
export function isClickedOutside(event, element) {
  if (event.target instanceof HTMLDialogElement || !(event.target instanceof Element)) {
    return !isPointWithinElement(event.clientX, event.clientY, element);
  }

  return !element.contains(event.target);
}

/**
 * Check if a point is within an element.
 * @param {number} x The x coordinate of the point.
 * @param {number} y The y coordinate of the point.
 * @param {Element} element The element to check.
 * @returns {boolean} True if the point is within the element, false otherwise.
 */
export function isPointWithinElement(x, y, element) {
  const { left, right, top, bottom } = element.getBoundingClientRect();

  return x >= left && x <= right && y >= top && y <= bottom;
}

/**
 * A media query for large screens
 * @type {MediaQueryList}
 */
export const mediaQueryLarge = matchMedia('(min-width: 750px)');

/**
 * Check if the current breakpoint is mobile
 * @returns {boolean} True if the current breakpoint is mobile, false otherwise
 */
export function isMobileBreakpoint() {
  return !mediaQueryLarge.matches;
}

/**
 * Check if the current breakpoint is desktop
 * @returns {boolean} True if the current breakpoint is desktop, false otherwise
 */
export function isDesktopBreakpoint() {
  return mediaQueryLarge.matches;
}

/**
 * Clamps a number between a minimum and maximum value.
 * @param {number} value - The input number to clamp.
 * @param {number} min - The minimum value.
 * @param {number} max - The maximum value.
 * @returns {number} The clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

/**
 * Calculates the center point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the center of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the center for. If not provided, returns both axes.
 * @template {('x' | 'y')} T
 * @param {T} [axis]
 * @returns {T extends ('x' | 'y') ? number : {x: number, y: number}} The center point along the axis or an object with x and y coordinates.
 */
export function center(element, axis) {
  const { left, width, top, height } = element.getBoundingClientRect();
  const point = {
    x: left + width / 2,
    y: top + height / 2,
  };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Calculates the start point of an element along the specified axis.
 * @param {Element} element - The DOM element to find the start of.
 * @param {'x' | 'y'} [axis] - The axis ('x' or 'y') to get the start for. If not provided, returns both axes.
 * @returns {number | {x: number, y: number}} The start point along the axis or an object with x and y coordinates.
 */
export function start(element, axis) {
  const { left, top } = element.getBoundingClientRect();
  const point = { x: left, y: top };

  if (axis) return /**  @type {any} */ (point[axis]);

  return /**  @type {any} */ (point);
}

/**
 * Finds the value in an array that is closest to a target value.
 * @param {number[]} values - An array of numbers.
 * @param {number} target - The target number to find the closest value to.
 * @returns {number} The value from the array closest to the target.
 */
export function closest(values, target) {
  return values.reduce(function (prev, curr) {
    return Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev;
  });
}

/**
 * Prevents the default action of an event.
 * @param {Event} event - The event to prevent the default action of.
 */
export function preventDefault(event) {
  event.preventDefault();
}

/**
 * Get the visible elements within a root element.
 * @template {Element} T
 * @param {Element} root - The element within which elements should be visible.
 * @param {T[] | undefined} elements - The elements to check for visibility.
 * @param {number} [ratio=1] - The minimum percentage of the element that must be visible.
 * @param {'x' | 'y'} [axis] - Whether to only check along 'x' axis, 'y' axis, or both if undefined.
 * @returns {T[]} An array containing the visible elements.
 */
export function getVisibleElements(root, elements, ratio = 1, axis) {
  if (!elements?.length) return [];
  const rootRect = root.getBoundingClientRect();

  return elements.filter((element) => {
    const { width, height, top, right, left, bottom } = element.getBoundingClientRect();

    if (ratio < 1) {
      const intersectionLeft = Math.max(rootRect.left, left);
      const intersectionRight = Math.min(rootRect.right, right);
      const intersectionWidth = Math.max(0, intersectionRight - intersectionLeft);

      if (axis === 'x') {
        return width > 0 && intersectionWidth / width >= ratio;
      }

      const intersectionTop = Math.max(rootRect.top, top);
      const intersectionBottom = Math.min(rootRect.bottom, bottom);
      const intersectionHeight = Math.max(0, intersectionBottom - intersectionTop);

      if (axis === 'y') {
        return height > 0 && intersectionHeight / height >= ratio;
      }

      const intersectionArea = intersectionWidth * intersectionHeight;
      const elementArea = width * height;

      // Check that at least the specified ratio of the element is visible
      return elementArea > 0 && intersectionArea / elementArea >= ratio;
    }

    const isWithinX = left >= rootRect.left && right <= rootRect.right;
    if (axis === 'x') {
      return isWithinX;
    }

    const isWithinY = top >= rootRect.top && bottom <= rootRect.bottom;
    if (axis === 'y') {
      return isWithinY;
    }

    return isWithinX && isWithinY;
  });
}

export function getIOSVersion() {
  const { userAgent } = navigator;
  const isIOS = /(iPhone|iPad)/i.test(userAgent);

  if (!isIOS) return null;

  const version = userAgent.match(/OS ([\d_]+)/)?.[1];
  const [major, minor] = version?.split('_') || [];
  if (!version || !major) return null;

  return {
    fullString: version.replace('_', '.'),
    major: parseInt(major, 10),
    minor: minor ? parseInt(minor, 10) : 0,
  };
}

/**
 * Determines which grid items should be animated during a transition.
 * It makes an estimation based on the zoom-out card size because it's
 * the common denominator for both transition states. I.e. transitioning either
 * from 10 to 20 cards the other way around, both need 20 cards to be animated.
 * @param {Element} grid - The grid element
 * @param {Element[]} cards - The cards to animate
 * @returns {number} - Number of cards that should be animated
 */
function getCardsToAnimate(grid, cards) {
  if (!grid || !cards || cards.length === 0) return 0;

  const itemSample = cards[0];
  if (!itemSample) return 0;

  // Calculate the visible area of the grid for the Y axis. Assume X is always fully visible:
  const gridRect = grid.getBoundingClientRect();
  const visibleArea = {
    top: Math.max(0, gridRect.top),
    bottom: Math.min(window.innerHeight, gridRect.bottom),
  };

  const visibleHeight = Math.round(visibleArea.bottom - visibleArea.top);
  if (visibleHeight <= 0) return 0;

  /** @type {import('product-card').ProductCard | null} */
  const cardSample = itemSample.querySelector('product-card');
  const gridStyle = getComputedStyle(grid);

  const galleryAspectRatio = cardSample?.refs?.cardGallery?.style.getPropertyValue('--gallery-aspect-ratio') || '';
  let aspectRatio = parseFloat(galleryAspectRatio) || 0.5;
  if (galleryAspectRatio?.includes('/')) {
    const [width = '1', height = '2'] = galleryAspectRatio.split('/');
    aspectRatio = parseInt(width, 10) / parseInt(height, 10);
  }

  const cardGap = parseInt(cardSample?.refs?.productCardLink?.style.getPropertyValue('--product-card-gap') || '') || 12;
  const gridGap = parseInt(gridStyle.getPropertyValue('--product-grid-gap')) || 12;

  // Assume only a couple of lines of text in the card details (title and price).
  // If the title wraps into more lines, we might just animate more cards, but that's fine.
  const detailsSize = ((parseInt(gridStyle.fontSize) || 16) + 2) * 2;

  const isMobile = window.innerWidth < 750;

  // Always use the zoom-out state card width
  const cardWidth = isMobile ? Math.round((gridRect.width - gridGap) / 2) : 100;
  const cardHeight = Math.round(cardWidth / aspectRatio) + cardGap + detailsSize;

  // Calculate the number of cards that fit in the visible area:
  // - The width estimation is pretty accurate, we can ignore decimals.
  // - The height estimation needs to account for peeking rows, so we round up.
  const columnsInGrid = isMobile ? 2 : Math.floor((gridRect.width + gridGap) / (cardWidth + gridGap));
  const rowsInGrid = Math.ceil((visibleHeight - gridGap) / (cardHeight + gridGap));

  return columnsInGrid * rowsInGrid;
}

/**
 * Preloads an image
 * @param {string} src - The source of the image to preload
 */
export function preloadImage(src) {
  const image = new Image();
  image.src = src;
}

export class TextComponent extends HTMLElement {
  shimmer() {
    this.setAttribute('shimmer', '');
  }
}

if (!customElements.get('text-component')) {
  customElements.define('text-component', TextComponent);
}

/**
 * Resets the shimmer attribute on all elements in the container.
 * @param {Element} [container] - The container to reset the shimmer attribute on.
 */
export function resetShimmer(container = document.body) {
  const shimmer = container.querySelectorAll('[shimmer]');
  shimmer.forEach((item) => item.removeAttribute('shimmer'));
}

/**
 * Change the meta theme color of the browser.
 * @param {string} color - The color value (e.g., 'rgb(255, 255, 255)')
 */
export function changeMetaThemeColor(color) {
  const metaThemeColor = document.head.querySelector('meta[name="theme-color"]');
  if (metaThemeColor && color) {
    metaThemeColor.setAttribute('content', color);
  }
}

/**
 * Gets the `view` URL search parameter value, if it exists.
 * Useful for Section Rendering API calls to get HTML markup for the correct template view.
 * Primarily used in testing alternative template views.
 * @returns {string | null} The view parameter value, or null if it doesn't exist
 */
export function getViewParameterValue() {
  return new URLSearchParams(window.location.search).get('view');
}

/**
 * Helper to parse integer with a default fallback
 * Handles the case where 0 is a valid value (not falsy)
 * @template {number|null} T
 * @param {string|number|null|undefined} value - The value to parse
 * @param {T} defaultValue - The default value (number or null)
 * @returns {number|T} The parsed integer or default value
 */
export function parseIntOrDefault(value, defaultValue) {
  if (value === null || value === undefined || value === '') {
    return defaultValue;
  }
  const parsed = parseInt(value.toString());
  return isNaN(parsed) ? defaultValue : parsed;
}

class Scheduler {
  /** @type {Set<() => void>} */
  #queue = new Set();
  /** @type {boolean} */
  #scheduled = false;

  /** @param {() => void} task */
  schedule = async (task) => {
    this.#queue.add(task);

    if (!this.#scheduled) {
      this.#scheduled = true;

      // Wait for any in-progress view transitions to finish
      if (viewTransition.current) await viewTransition.current;

      requestAnimationFrame(this.flush);
    }
  };

  flush = () => {
    for (const task of this.#queue) {
      setTimeout(task, 0);
    }

    this.#queue.clear();
    this.#scheduled = false;
  };
}

export const scheduler = new Scheduler();

/**
 * Executes a callback once per session when in the Shopify theme editor
 * @param {HTMLElement} element - The element to check for the shopify editor block id
 * @param {string} sessionKeyName - Unique key for the session storage
 * @param {() => void} callback - Function to execute
 * @returns {void} - Void if the callback was executed, undefined if it wasn't
 */
export function oncePerEditorSession(element, sessionKeyName, callback) {
  const isInThemeEditor = window.Shopify?.designMode;
  const shopifyEditorSectionId = JSON.parse(element.dataset.shopifyEditorSection || '{}').id;
  const shopifyEditorBlockId = JSON.parse(element.dataset.shopifyEditorBlock || '{}').id;
  const editorId = shopifyEditorSectionId || shopifyEditorBlockId;
  const uniqueSessionKey = `${sessionKeyName}-${editorId}`;

  if (isInThemeEditor && sessionStorage.getItem(uniqueSessionKey)) return;

  callback();

  if (isInThemeEditor) sessionStorage.setItem(uniqueSessionKey, 'true');

  return;
}

/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

// Header calculation functions for maintaining CSS variables
export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;
  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

/**
 * Updates CSS custom properties for transparent header offset calculation
 * Avoids expensive :has() selectors
 */
function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');
  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');

  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';
  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

/**
 * Initialize and maintain header height CSS variables.
 */
function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  // Calculate initial heights
  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

export function updateAllHeaderCustomProperties() {
  updateHeaderHeights();
  updateTransparentHeaderOffset();
}

// Theme is not defined in some layouts, like the gift card page
if (typeof Theme !== 'undefined') {
  Theme.utilities = {
    ...Theme.utilities,
    scheduler: scheduler,
  };
}





/* === HOMEPAGE HERO (Desktop, 02.07.2026): textfreies Video + korrekt
   interpunktierter Claim als Overlay. Hintergrund: Das Update von
   templates/index.json liegt korrekt auf dem Server, aber Shopifys Renderer
   liefert eine alte Template-Kopie aus (Template-JSON-Propagations-Falle,
   siehe CLAUDE.md) — daher hier als Asset-JS, analog zum Mobile-Swap unten.
   Idempotent: tauscht nur, wenn noch das alte Video (33a3e80…) geliefert wird,
   und legt den Claim nur an, wenn er fehlt. Overlay <750px per CSS versteckt. */
(function() {
  if (window.location.pathname !== '/' && window.location.pathname !== '') return;
  var newUrl = 'https://cdn.shopify.com/videos/c/vp/ea0e0ee4d5d34d99bc5266105feeb631/ea0e0ee4d5d34d99bc5266105feeb631.HD-1080p-4.8Mbps-87971958.mp4';
  var newPoster = 'https://cdn.shopify.com/s/files/1/0959/8966/2083/files/preview_images/ea0e0ee4d5d34d99bc5266105feeb631.thumbnail.0000000000.jpg?v=1783006434';

  function fixHero() {
    var wrap = document.querySelector('div[style*="height: 80vh"]');
    if (!wrap) return;
    var video = wrap.querySelector('video');
    if (video && window.innerWidth >= 750) {
      var source = video.querySelector('source');
      if (source && source.src.indexOf('ea0e0ee4') === -1) {
        source.src = newUrl;
        video.setAttribute('poster', newPoster);
        video.load();
      }
    }
    if (!wrap.querySelector('.elmeda-hero-claim')) {
      var claim = document.createElement('div');
      claim.className = 'elmeda-hero-claim';
      var t = document.createElement('p');
      t.className = 'elmeda-hero-claim__title';
      t.textContent = 'Mode, die schützt';
      var s = document.createElement('p');
      s.className = 'elmeda-hero-claim__sub';
      s.textContent = 'Sicherheit, die man nicht sieht';
      claim.appendChild(t); claim.appendChild(s);
      wrap.appendChild(claim);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixHero);
  } else {
    fixHero();
  }
})();

/* === HOMEPAGE HERO: Swap to portrait video on mobile === */
(function() {
  if (window.innerWidth >= 750) return;
  if (window.location.pathname !== '/' && window.location.pathname !== '') return;

  var portraitUrl = 'https://cdn.shopify.com/videos/c/vp/752fd71a1377422bb09a5eb960b05770/752fd71a1377422bb09a5eb960b05770.HD-720p-4.5Mbps-82535113.mp4';
  var portraitPoster = 'https://cdn.shopify.com/s/files/1/0959/8966/2083/files/preview_images/752fd71a1377422bb09a5eb960b05770.thumbnail.0000000000.jpg?v=1776975865';

  function swapHeroVideo() {
    var heroVideo = document.querySelector('div[style*="height: 80vh"] video');
    if (!heroVideo) return;
    var source = heroVideo.querySelector('source');
    if (source) source.src = portraitUrl;
    heroVideo.setAttribute('poster', portraitPoster);
    heroVideo.load();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', swapHeroVideo);
  } else {
    swapHeroVideo();
  }
})();

/* === PRODUCT PAGE: Video Banner (Portrait 9:16, April 2026) === */
(function() {
  var bannerVideos = {
    'elmeda-protect-hose': {
      url: 'https://cdn.shopify.com/videos/c/vp/752fd71a1377422bb09a5eb960b05770/752fd71a1377422bb09a5eb960b05770.HD-1080p-7.2Mbps-82535113.mp4',
      poster: 'https://cdn.shopify.com/s/files/1/0959/8966/2083/files/preview_images/752fd71a1377422bb09a5eb960b05770.thumbnail.0000000000.jpg?v=1776975865'
    },
    'elmeda-hose-mit-seitenreissverschluss': {
      url: 'https://cdn.shopify.com/videos/c/vp/244ff71174f24349a1737e5d6270ec14/244ff71174f24349a1737e5d6270ec14.HD-1080p-7.2Mbps-82535107.mp4',
      poster: 'https://cdn.shopify.com/s/files/1/0959/8966/2083/files/preview_images/244ff71174f24349a1737e5d6270ec14.thumbnail.0000000000.jpg?v=1776975863'
    },
    'elmeda-kurzhose-mit-seitenreissverschluss': {
      url: 'https://cdn.shopify.com/videos/c/vp/244ff71174f24349a1737e5d6270ec14/244ff71174f24349a1737e5d6270ec14.HD-1080p-7.2Mbps-82535107.mp4',
      poster: 'https://cdn.shopify.com/s/files/1/0959/8966/2083/files/preview_images/244ff71174f24349a1737e5d6270ec14.thumbnail.0000000000.jpg?v=1776975863'
    }
  };

  function insertBannerVideo() {
    var path = window.location.pathname;
    var matchedHandle = null;
    for (var h in bannerVideos) {
      if (path.indexOf(h) !== -1) { matchedHandle = h; break; }
    }
    if (!matchedHandle || document.getElementById('elmeda-video-banner')) return;

    var productSection = document.querySelector('.shopify-section .product-information');
    if (!productSection) return;
    var shopifySection = productSection.closest('.shopify-section');
    if (!shopifySection) return;

    var cfg = bannerVideos[matchedHandle];
    var banner = document.createElement('div');
    banner.id = 'elmeda-video-banner';
    banner.style.cssText = 'position:relative;width:100%;max-width:500px;margin:0 auto 0;aspect-ratio:9/16;overflow:hidden;';

    var video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('poster', cfg.poster);
    video.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';

    var source = document.createElement('source');
    source.src = cfg.url;
    source.type = 'video/mp4';
    video.appendChild(source);
    banner.appendChild(video);

    /* 02.07.2026: Banner NACH der Produkt-Sektion einfügen (vorher davor) —
       der 889px/693px hohe Autoplay-Banner drückte Titel/Preis/Kaufbutton auf
       Desktop UND Mobil unter den Fold (Conversion-Befund 30.06.2026). Das Video
       bleibt auf der Seite (zusätzlich läuft es als Player unter den CTA-Buttons). */
    shopifySection.parentElement.insertBefore(banner, shopifySection.nextSibling);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', insertBannerVideo);
  } else {
    insertBannerVideo();
  }
})();

/* === PRODUCT PAGE: Video below CTA buttons (Portrait 9:16, April 2026) === */
(function() {
  var videoMap = {
    'elmeda-protect-hose': 'https://cdn.shopify.com/videos/c/vp/752fd71a1377422bb09a5eb960b05770/752fd71a1377422bb09a5eb960b05770.HD-1080p-7.2Mbps-82535113.mp4',
    'elmeda-hose-mit-seitenreissverschluss': 'https://cdn.shopify.com/videos/c/vp/244ff71174f24349a1737e5d6270ec14/244ff71174f24349a1737e5d6270ec14.HD-1080p-7.2Mbps-82535107.mp4',
    'elmeda-kurzhose-mit-seitenreissverschluss': 'https://cdn.shopify.com/videos/c/vp/244ff71174f24349a1737e5d6270ec14/244ff71174f24349a1737e5d6270ec14.HD-1080p-7.2Mbps-82535107.mp4'
  };
  
  function insertProductVideo() {
    var path = window.location.pathname;
    var handle = null;
    for (var h in videoMap) {
      if (path.indexOf(h) !== -1) { handle = h; break; }
    }
    if (!handle || document.getElementById('elmeda-product-video')) return;
    
    var buttons = document.querySelector('.product-form-buttons');
    if (!buttons) return;
    
    var container = document.createElement('div');
    container.id = 'elmeda-product-video';
    container.style.cssText = 'margin-top: 24px; width: 100%; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 12px rgba(0,0,0,0.06);';
    
    var video = document.createElement('video');
    video.controls = true;
    video.playsInline = true;
    video.preload = 'none';
    video.style.cssText = 'width: 100%; display: block; border-radius: 8px;';
    
    var source = document.createElement('source');
    source.src = videoMap[handle];
    source.type = 'video/mp4';
    video.appendChild(source);
    container.appendChild(video);
    
    buttons.parentNode.insertBefore(container, buttons.nextSibling);
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', insertProductVideo);
  } else {
    insertProductVideo();
  }
})();


/* === Hide video in product gallery (shown via JS player below CTA) === */
(function() {
  function hideGalleryVideos() {
    // Hide video thumbnails (identified by preview_images in src)
    document.querySelectorAll('.slideshow-controls__thumbnail').forEach(function(btn) {
      var img = btn.querySelector('img');
      if (img && img.src && img.src.indexOf('preview_images') !== -1) {
        btn.style.display = 'none';
      }
    });
    // Hide video slides in main slideshow
    document.querySelectorAll('media-gallery slideshow-slide').forEach(function(slide) {
      if (slide.querySelector('video, .product-media--video')) {
        slide.style.display = 'none';
      }
      // Also check for video poster images
      var img = slide.querySelector('img');
      if (img && img.src && img.src.indexOf('preview_images') !== -1) {
        slide.style.display = 'none';
      }
    });
    document.querySelectorAll('.product-media-container--video').forEach(function(el) {
      el.style.display = 'none';
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(hideGalleryVideos, 500); });
  } else {
    setTimeout(hideGalleryVideos, 500);
  }
})();





/* === PRODUCT DESCRIPTION: Full-width layout with section grid === */
(function() {
  function layoutDescription() {
    var formatter = document.querySelector('.product-details rte-formatter');
    if (!formatter) return;
    
    var h4s = formatter.querySelectorAll('h4');
    if (h4s.length < 2) return;
    
    /* Already processed? */
    if (document.getElementById('elmeda-desc-fullwidth')) return;
    
    /* Collect intro (everything before first h4) and sections */
    var introNodes = [];
    var sections = [];
    var currentSection = null;
    
    var children = Array.from(formatter.children);
    var foundFirstH4 = false;
    
    for (var i = 0; i < children.length; i++) {
      var el = children[i];
      if (el.tagName === 'H4') {
        foundFirstH4 = true;
        if (currentSection) sections.push(currentSection);
        currentSection = {heading: el.outerHTML, content: ''};
      } else if (!foundFirstH4) {
        introNodes.push(el.outerHTML);
      } else if (currentSection) {
        currentSection.content += el.outerHTML;
      }
    }
    if (currentSection) sections.push(currentSection);
    
    /* Build new full-width block */
    var wrapper = document.createElement('div');
    wrapper.id = 'elmeda-desc-fullwidth';
    
    /* Intro */
    var introHtml = '<div class="elmeda-desc-intro">' + introNodes.join('') + '</div>';
    
    /* Section grid */
    var gridHtml = '<div class="elmeda-desc-sections">';
    for (var j = 0; j < sections.length; j++) {
      gridHtml += '<div class="elmeda-desc-section">' + sections[j].heading + sections[j].content + '</div>';
    }
    gridHtml += '</div>';
    
    wrapper.innerHTML = introHtml + gridHtml;
    
    /* Insert AFTER the shopify-section containing the product (outside product-information grid) */
    var productSection = document.querySelector('.product-information');
    if (productSection) {
      var shopifySection = productSection.closest('.shopify-section');
      var insertTarget = shopifySection || productSection;
      insertTarget.parentElement.insertBefore(wrapper, insertTarget.nextSibling);
      
      /* Hide only the rte-formatter text */
      formatter.style.display = 'none';
    }
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(layoutDescription, 600); });
  } else {
    setTimeout(layoutDescription, 600);
  }
})();



/* === VERFUEGBARKEITS-HINWEIS 50901 (10.06.2026): 17/18 Varianten Bestand 0,
   Inventar nur via Admin-UI pflegbar — Hinweis entfernen sobald Bestand da === */
(function() {
  function addAvailabilityNotice() {
    if (window.location.pathname.indexOf('elmeda-hose-mit-seitenreissverschluss') === -1) return;
    if (document.getElementById('elmeda-availability-notice')) return;
    var anchor = document.querySelector('.product-information');
    if (!anchor) { setTimeout(addAvailabilityNotice, 500); return; }
    var note = document.createElement('div');
    note.id = 'elmeda-availability-notice';
    note.setAttribute('role', 'status');
    note.textContent = 'Aktuell ist diese Hose nur in Größe 38 in Marine lieferbar; viele Größen sind derzeit vergriffen. Bei Fragen zur Verfügbarkeit Ihrer Größe schreiben Sie uns gern an kontakt@elmeda.de.';
    anchor.parentNode.insertBefore(note, anchor);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addAvailabilityNotice);
  } else {
    addAvailabilityNotice();
  }
})();

/* === BARRIEREFREIHEIT: Accessibility Enhancements (WCAG 2.1 AA) === */
(function() {
  function enhanceAccessibility() {

    /* --- Skip-to-Content Link (erstes fokussierbares Element) --- */
    if (!document.querySelector('.elmeda-skip-link')) {
      var skip = document.createElement('a');
      skip.className = 'elmeda-skip-link';
      skip.href = '#MainContent';
      skip.textContent = 'Zum Inhalt springen';
      document.body.insertBefore(skip, document.body.firstChild);
    }

    /* --- SVG Icons: role="img" + aria-label --- */
    var svgLabels = {
      'Unsichtbarer Schutz': 'Schild-Symbol: Unsichtbarer Schutz',
      'Modisches Design': 'Diamant-Symbol: Modisches Design',
      'Premium-Qualität': 'Qualitätssiegel-Symbol: Premium-Qualität',
      'Kostenloser Versand': 'Lieferwagen-Symbol: Kostenloser Versand',
      '14 Tage Widerrufsrecht': 'Rückgabe-Symbol: 14 Tage Widerrufsrecht',
      'Sichere Zahlung': 'Schild-Symbol: Sichere Zahlung',
      'Diskreter Versand': 'Paket-Symbol: Diskreter Versand'
    };

    document.querySelectorAll('.elmeda-usps-item, .elmeda-trust-item').forEach(function(item) {
      var svg = item.querySelector('svg');
      if (!svg || svg.getAttribute('aria-label')) return;
      var textEl = item.querySelector('h3, p');
      if (!textEl) return;
      var text = textEl.textContent.trim();
      var label = svgLabels[text] || text;
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', label);
    });

    /* --- Material-Toggle Buttons: aria-pressed --- */
    var matSection = document.querySelector('.elmeda-material, .elmeda-material-grid');
    if (matSection) {
      var toggleBtns = matSection.querySelectorAll('button');
      toggleBtns.forEach(function(btn, i) {
        if (btn.getAttribute('aria-pressed') !== null) return;
        btn.setAttribute('aria-pressed', i === 0 ? 'true' : 'false');
        btn.addEventListener('click', function() {
          toggleBtns.forEach(function(b) { b.setAttribute('aria-pressed', 'false'); });
          btn.setAttribute('aria-pressed', 'true');
        });
      });
    }

    /* --- Dekorative Videos: role="presentation" (autoplay, muted, kein Inhalt) --- */
    document.querySelectorAll('video[autoplay]').forEach(function(v) {
      if (!v.getAttribute('role')) {
        v.setAttribute('role', 'presentation');
        v.setAttribute('aria-hidden', 'true');
      }
    });

    /* --- Produktvideo (mit Controls): aria-label --- */
    var productVideo = document.querySelector('#elmeda-product-video video');
    if (productVideo && !productVideo.getAttribute('aria-label')) {
      productVideo.setAttribute('aria-label', 'ELMEDA Produktvideo');
    }

    /* --- Banner-Video: explizit dekorativ markieren --- */
    var bannerVideo = document.querySelector('#elmeda-video-banner video');
    if (bannerVideo && !bannerVideo.getAttribute('role')) {
      bannerVideo.setAttribute('role', 'presentation');
      bannerVideo.setAttribute('aria-hidden', 'true');
    }
  }

  /* Verzögerter Start: Custom Elements und dynamisch eingefügte Elemente abwarten */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() { setTimeout(enhanceAccessibility, 1000); });
  } else {
    setTimeout(enhanceAccessibility, 1000);
  }
})();

/* === ELMEDA SEO: Rich-Results JSON-LD (BreadcrumbList + WebSite/SearchAction) — 20.07.2026 ===
   Autonome Audit-Maßnahmen Rang 11 + 12 (SEO-AUDIT-20260702.md). Rein technisch, kein Marken-
   Wortlaut. JS-Injektion, weil Horizon-CDN nur Assets zuverlässig propagiert (theme.liquid-Quirk). */
(function elmedaRichResults() {
  var ORIGIN = 'https://elmeda.de';

  function injectLd(obj, id) {
    if (document.getElementById(id)) return;
    var s = document.createElement('script');
    s.type = 'application/ld+json';
    s.id = id;
    s.textContent = JSON.stringify(obj);
    (document.head || document.documentElement).appendChild(s);
  }

  /* Marken-Suffix (" – ELMEDA …", " | ELMEDA …") vom Titel abtrennen */
  function cleanName(str) {
    return (str || '').replace(/\s*[|–—-]\s*ELMEDA.*$/i, '').trim();
  }

  /* Name der aktuellen Seite: sichtbares H1 bevorzugt, sonst og:title, sonst <title> */
  function currentPageName() {
    var h1 = document.querySelector('h1');
    if (h1 && h1.textContent.trim()) return h1.textContent.trim();
    var og = document.querySelector('meta[property="og:title"]');
    if (og && og.content) return cleanName(og.content);
    return cleanName(document.title);
  }

  function build() {
    var path = window.location.pathname;

    /* Rang 12 — WebSite + SearchAction NUR auf der Startseite (Sitelinks-Searchbox) */
    if (path === '/' || path === '') {
      injectLd({
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "ELMEDA",
        "url": ORIGIN + "/",
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": ORIGIN + "/search?q={search_term_string}"
          },
          "query-input": "required name=search_term_string"
        }
      }, 'elmeda-website-jsonld');
      return;
    }

    /* Rang 11 — BreadcrumbList generisch aus dem Pfad (Produkt/Collection/Artikel) */
    var items = [{ "@type": "ListItem", "position": 1, "name": "Startseite", "item": ORIGIN + "/" }];
    var pos = 2;

    if (path.indexOf('/products/') === 0) {
      items.push({ "@type": "ListItem", "position": pos++, "name": "Produkte", "item": ORIGIN + "/collections/produkte" });
      items.push({ "@type": "ListItem", "position": pos++, "name": currentPageName(), "item": ORIGIN + path });
    } else if (path.indexOf('/blogs/') === 0) {
      var segs = path.split('/').filter(Boolean); // ['blogs','ratgeber','<handle>']
      if (segs.length >= 2) {
        var blogName = segs[1].charAt(0).toUpperCase() + segs[1].slice(1);
        var isLeaf = segs.length < 3;
        items.push({ "@type": "ListItem", "position": pos++,
          "name": isLeaf ? currentPageName() : blogName,
          "item": ORIGIN + '/' + segs[0] + '/' + segs[1] });
      }
      if (segs.length >= 3) {
        items.push({ "@type": "ListItem", "position": pos++, "name": currentPageName(), "item": ORIGIN + path });
      }
    } else if (path.indexOf('/collections/') === 0) {
      items.push({ "@type": "ListItem", "position": pos++, "name": currentPageName(), "item": ORIGIN + path });
    } else {
      return; // sonstige Seiten: keine Breadcrumb
    }

    if (items.length >= 2) {
      injectLd({ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": items }, 'elmeda-breadcrumb-jsonld');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', build);
  } else {
    build();
  }
})();
